import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Download,
  Pencil,
  Plus,
  Receipt,
  RotateCcw,
  Scale,
  Search,
  Trash2,
  Wallet,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import './PaymentTrucking.css';
import {
  fetchRentalPos,
  fetchRentalInvoices,
  fetchRentalCompanies,
  createRentalPo,
  updateRentalPo,
  createRentalInvoice,
  updateRentalInvoice,
  deleteRentalInvoice,
  createRentalCompany
} from '../services/rentalPaymentService';
import { syncAllFleetPosFromCarRentals } from '../services/fleetPoCarRentalSync';

const ITEMS_PER_PAGE = 10;

/** PO balance below this (TZS) is highlighted red. */
const PO_BALANCE_LOW_THRESHOLD = 2000000;

const TAB_OPTIONS = [
  { key: 'po', label: 'Rental PO Tracking', icon: ClipboardList },
  { key: 'invoice', label: 'Rental Invoice Tracking', icon: Receipt },
  { key: 'companies', label: 'Rental Companies', icon: Building2 }
];

/** Project options for Record PO and project filter list. */
const RENTAL_PO_PROJECT_OPTIONS = [
  'ADMIN',
  'BTS',
  'HELIOS',
  'FTTM',
  'REA 2C',
  'HEP IIB (Lot 8)',
  'HEP IIB (Lot 10)'
];

/** Default invoice statuses (merged with values from data). */
const RENTAL_INVOICE_STATUS_OPTIONS = [
  'Paid',
  'Pending Signature',
  'Admin review',
  'Pending Payment'
];

/** Base rental company choices always offered in the Record PO modal. */
const BASE_RENTAL_SUPPLIER_OPTIONS = ['Smiles', 'Satguru'];

/** Merge Smile / Smiles Rent a Car variants into "Smiles". */
function normalizeRentalCompanyLabel(name) {
  const t = String(name || '').trim();
  if (!t) return '';
  const n = t.toLowerCase().replace(/\s+/g, ' ');
  if (/^smiles?(\s+rent[\s-]*a[\s-]*car)?$/.test(n) || (n.includes('smile') && n.includes('rent'))) {
    return 'Smiles';
  }
  return t;
}

/** Merge equivalent HEP project labels (LOT 10 ≡ (Lot 10)). */
function normalizeRentalProjectLabel(project) {
  const t = String(project || '').trim();
  if (!t) return '';
  const compact = t.toLowerCase().replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
  if (compact === 'hep iib lot 10') return 'HEP IIB (Lot 10)';
  if (compact === 'hep iib lot 8') return 'HEP IIB (Lot 8)';
  return t;
}

/** CSS modifier for colored project pills (tables). */
function rentalProjectBadgeClass(project) {
  const t = normalizeRentalProjectLabel(project);
  if (!t) return 'rp-project--empty';
  const key = t.toLowerCase().replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
  const known = {
    admin: 'rp-project--admin',
    bts: 'rp-project--bts',
    helios: 'rp-project--helios',
    fttm: 'rp-project--fttm',
    'rea 2c': 'rp-project--rea2c',
    'hep iib lot 8': 'rp-project--hep8',
    'hep iib lot 10': 'rp-project--hep10'
  };
  if (known[key]) return known[key];
  let h = 0;
  for (let i = 0; i < t.length; i += 1) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return `rp-project--alt${h % 6}`;
}

function RentalProjectBadge({ project }) {
  const label = normalizeRentalProjectLabel(project);
  if (!label) {
    return <span className="rp-project-badge rp-project--empty">—</span>;
  }
  return (
    <span className={`rp-project-badge ${rentalProjectBadgeClass(label)}`}>{label}</span>
  );
}

/** VAT 18% on net Amount; Invoice Amount (gross) = Amount + VAT. PO "Total Amount Invoice" uses net (excl. VAT). */
const RENTAL_VAT_RATE = 0.18;

function rentalNet(amount) {
  return Number(amount) || 0;
}

function rentalVat(amount) {
  return rentalNet(amount) * RENTAL_VAT_RATE;
}

/** Gross = net Amount + 18% VAT (same as Invoice Amount column in the invoice table). */
function rentalInvoiceGross(amount) {
  const net = rentalNet(amount);
  return net + net * RENTAL_VAT_RATE;
}

/** When `vatApplies === false`, Amount is treated as full invoice value with no separate VAT line. */
function invoiceVatForRow(row) {
  if (row.vatApplies === false) return 0;
  return rentalVat(row.amount);
}

function invoiceGrossForRow(row) {
  if (row.vatApplies === false) return rentalNet(row.amount);
  return rentalInvoiceGross(row.amount);
}

/** True only when status is paid (case-insensitive, trimmed). All other rows count toward pending. */
function isRentalInvoicePaid(row) {
  return String(row.status || '').trim().toLowerCase() === 'paid';
}

/** Sum of net (excl. VAT) for invoice lines on this PO within the given list (e.g. filter-scoped rows). */
function sumNetInvoicedForPo(invoices, poNumber) {
  return invoices
    .filter((inv) => inv.poNumber === poNumber)
    .reduce((sum, inv) => sum + rentalNet(inv.amount), 0);
}

/** "January 2026" → "2026-01" for month filter when invoice_date is missing or odd. */
function yearMonthFromInvoiceMonthLabel(label) {
  if (!label || typeof label !== 'string') return '';
  const t = label.trim();
  const months = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12
  };
  const m = t.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (m) {
    const mi = months[m[1].toLowerCase()];
    if (mi) return `${m[2]}-${String(mi).padStart(2, '0')}`;
  }
  return '';
}

/** Invoice calendar month as yyyy-mm for month/year filter (same as the month picker value). */
function invoiceYearMonthKey(row) {
  const raw = row.invoiceDate;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const y = raw.getFullYear();
    const mo = raw.getMonth() + 1;
    return `${y}-${String(mo).padStart(2, '0')}`;
  }
  const d = String(raw ?? '')
    .trim()
    .replace(/^null$/i, '')
    .replace(/^undefined$/i, '');
  if (!d) return yearMonthFromInvoiceMonthLabel(row.invoiceMonth);
  const head = d.includes('T') ? d.split('T')[0] : d;
  if (/^\d{4}-\d{2}-\d{2}/.test(head)) return head.slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(head)) return head;
  const slash = head.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})/);
  if (slash) return `${slash[1]}-${String(slash[2]).padStart(2, '0')}`;
  const ym = yearMonthFromInvoiceMonthLabel(row.invoiceMonth);
  return ym;
}

/** Convert yyyy-mm (month input) to e.g. "March 2026" for invoice month column. */
function formatInvoiceMonthFromYm(ym) {
  if (!ym || typeof ym !== 'string' || ym.length < 7) return '';
  const parts = ym.split('-');
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return '';
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

/** Row invoice month label → yyyy-mm for the month picker. */
function invoiceMonthToYm(row) {
  const fromKey = invoiceYearMonthKey(row);
  if (/^\d{4}-\d{2}$/.test(fromKey)) return fromKey;
  return '';
}

/** Invoice table: show first 8 characters; full rental company on hover (`title`). */
function rentalCompanyCellShort(name) {
  const s = String(name ?? '').trim();
  if (!s) return '—';
  if (s.length <= 8) return s;
  return `${s.slice(0, 8)}…`;
}

function getStatusClass(status) {
  if (!status) return '';
  return status.toLowerCase().replace(/\s+/g, '-');
}

function getPageNumbers(totalPages, safePage) {
  const pageNumbers = [];
  const maxVisiblePages = 5;
  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else if (safePage <= 3) {
    for (let i = 1; i <= 4; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  } else if (safePage >= totalPages - 2) {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  }
  return pageNumbers;
}

function RpPagination({ totalFiltered, safePage, totalPages, indexOfFirstItem, indexOfLastItem, onPageChange }) {
  if (totalFiltered <= 0) return null;

  const goToPage = (page) => {
    onPageChange(page);
    const wrapper = document.querySelector('.rp-table-wrapper');
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  return (
    <div className="rp-pagination">
      <div className="rp-pagination__info">
        Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
      </div>
      <div className="rp-pagination__controls">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rp-soft-btn h-8 w-8"
          onClick={() => safePage > 1 && goToPage(safePage - 1)}
          disabled={safePage === 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="rp-pagination__numbers">
          {getPageNumbers(totalPages, safePage).map((pageNum, index) =>
            pageNum === '...' ? (
              <span key={`ellipsis-${index}`} className="rp-pagination__ellipsis">
                …
              </span>
            ) : (
              <button
                key={pageNum}
                type="button"
                className={`rp-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                onClick={() => goToPage(pageNum)}
              >
                {pageNum}
              </button>
            )
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rp-soft-btn h-8 w-8"
          onClick={() => safePage < totalPages && goToPage(safePage + 1)}
          disabled={safePage === totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function PaymentTrucking() {
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [rentalCompanyFilter, setRentalCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  /** 'all' or yyyy-mm (month picker) — filters invoices and derived PO / company totals. */
  const [monthYearFilter, setMonthYearFilter] = useState('all');
  const [activeTable, setActiveTable] = useState('po');
  const [currentPage, setCurrentPage] = useState(1);
  const [poRows, setPoRows] = useState([]);
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [rentalCompanyDocs, setRentalCompanyDocs] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState('');
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState('');
  const [editStatus, setEditStatus] = useState('Pending Signature');
  const [editDatePaid, setEditDatePaid] = useState('');
  /** Inline PR No edit: invoice number key + draft value. */
  const [editingPrInvoiceNumber, setEditingPrInvoiceNumber] = useState('');
  const [editPrNoValue, setEditPrNoValue] = useState('');
  /** Inline invoice month edit (yyyy-mm). */
  const [editingMonthInvoiceNumber, setEditingMonthInvoiceNumber] = useState('');
  const [editInvoiceMonthYm, setEditInvoiceMonthYm] = useState('');
  /** Inline PO field edit: poNumber + field ('sd' | 'totalPoAmount') + draft. */
  const [editingPoNumber, setEditingPoNumber] = useState('');
  const [editingPoField, setEditingPoField] = useState('');
  const [editPoDraft, setEditPoDraft] = useState('');
  const [poFieldSaving, setPoFieldSaving] = useState(false);
  const [showRecordPoModal, setShowRecordPoModal] = useState(false);
  const [recordPoError, setRecordPoError] = useState('');
  const [newPoForm, setNewPoForm] = useState({
    poNumber: '',
    supplier: 'Smiles',
    project: '',
    sd: '',
    totalPoAmount: ''
  });
  const [showRecordInvoiceModal, setShowRecordInvoiceModal] = useState(false);
  const [recordInvoiceError, setRecordInvoiceError] = useState('');
  const [newInvoiceForm, setNewInvoiceForm] = useState({
    invoiceNumber: '',
    prNo: '',
    poNumber: '',
    invoiceDate: '',
    invoiceMonthYm: '',
    amount: '',
    vatApplies: true,
    status: 'Pending Signature',
    datePaid: ''
  });
  const [showAddRentalCompanyModal, setShowAddRentalCompanyModal] = useState(false);
  const [addRentalCompanyError, setAddRentalCompanyError] = useState('');
  const [newRentalCompanyName, setNewRentalCompanyName] = useState('');

  const loadRentalData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setDataLoading(true);
        setDataError('');
      }
      const [pos, invs, comps] = await Promise.all([
        fetchRentalPos(),
        fetchRentalInvoices(),
        fetchRentalCompanies()
      ]);
      setPoRows(pos);
      setInvoiceRows(invs);
      setRentalCompanyDocs(comps);
    } catch (err) {
      setDataError(err?.message || String(err));
      if (!silent) {
        setPoRows([]);
        setInvoiceRows([]);
        setRentalCompanyDocs([]);
      }
    } finally {
      if (!silent) setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadRentalData();
      if (cancelled) return;
      // Sync Car Rental POs in the background — do not block the initial table load.
      try {
        await syncAllFleetPosFromCarRentals();
        if (!cancelled) await loadRentalData({ silent: true });
      } catch (syncErr) {
        console.warn('Car rental → Fleet PO sync:', syncErr);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRentalData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTable, appliedSearch, projectFilter, rentalCompanyFilter, statusFilter, monthYearFilter]);

  const poSupplierByNumber = useMemo(
    () => Object.fromEntries(poRows.map((p) => [p.poNumber, p.supplier])),
    [poRows]
  );

  const poProjectByNumber = useMemo(
    () => Object.fromEntries(poRows.map((p) => [p.poNumber, p.project])),
    [poRows]
  );

  const projects = useMemo(() => {
    const values = new Set([
      ...RENTAL_PO_PROJECT_OPTIONS,
      ...poRows.map((row) => normalizeRentalProjectLabel(row.project)),
      ...invoiceRows.map((row) => normalizeRentalProjectLabel(row.project))
    ]);
    values.delete('');
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [invoiceRows, poRows]);

  const rentalCompanies = useMemo(() => {
    const values = new Set();
    poRows.forEach((row) => {
      const s = normalizeRentalCompanyLabel(row.supplier);
      if (s) values.add(s);
    });
    invoiceRows.forEach((inv) => {
      const s = normalizeRentalCompanyLabel(
        poSupplierByNumber[inv.poNumber] || inv.supplier
      );
      if (s) values.add(s);
    });
    rentalCompanyDocs.forEach((c) => {
      const s = normalizeRentalCompanyLabel(c.rentalName);
      if (s) values.add(s);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [invoiceRows, poRows, poSupplierByNumber, rentalCompanyDocs]);

  const poSupplierOptions = useMemo(() => {
    const set = new Set(BASE_RENTAL_SUPPLIER_OPTIONS.map(normalizeRentalCompanyLabel));
    rentalCompanies.forEach((name) => {
      const s = normalizeRentalCompanyLabel(name);
      if (s) set.add(s);
    });
    set.delete('');
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rentalCompanies]);

  const invoiceStatuses = useMemo(() => {
    const values = new Set([
      ...RENTAL_INVOICE_STATUS_OPTIONS,
      ...invoiceRows.map((row) => String(row.status || '').trim()).filter(Boolean)
    ]);
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [invoiceRows]);

  const resolvedInvoices = useMemo(
    () =>
      invoiceRows.map((row) => ({
        ...row,
        supplier: poSupplierByNumber[row.poNumber] || row.supplier,
        project: row.project || poProjectByNumber[row.poNumber] || ''
      })),
    [invoiceRows, poProjectByNumber, poSupplierByNumber]
  );

  const filteredInvoiceRows = useMemo(() => {
    const query = appliedSearch.trim().toLowerCase();

    return resolvedInvoices.filter((row) => {
      if (
        projectFilter !== 'all' &&
        normalizeRentalProjectLabel(row.project) !== normalizeRentalProjectLabel(projectFilter)
      ) {
        return false;
      }
      if (
        rentalCompanyFilter !== 'all' &&
        normalizeRentalCompanyLabel(row.supplier) !==
          normalizeRentalCompanyLabel(rentalCompanyFilter)
      ) {
        return false;
      }
      if (statusFilter !== 'all' && String(row.status || '').trim() !== statusFilter) return false;
      if (monthYearFilter !== 'all' && monthYearFilter) {
        if (invoiceYearMonthKey(row) !== monthYearFilter) return false;
      }
      if (!query) return true;

      return (
        row.invoiceNumber.toLowerCase().includes(query) ||
        (row.prNo || '').toLowerCase().includes(query) ||
        row.poNumber.toLowerCase().includes(query) ||
        (row.project || '').toLowerCase().includes(query) ||
        row.supplier.toLowerCase().includes(query) ||
        row.invoiceDate.toLowerCase().includes(query) ||
        row.invoiceMonth.toLowerCase().includes(query) ||
        row.status.toLowerCase().includes(query) ||
        (row.datePaid || '').toLowerCase().includes(query) ||
        (row.remark || '').toLowerCase().includes(query)
      );
    });
  }, [appliedSearch, monthYearFilter, projectFilter, rentalCompanyFilter, resolvedInvoices, statusFilter]);

  const filteredPoRows = useMemo(() => {
    const query = appliedSearch.trim().toLowerCase();

    return poRows
      .map((row) => {
        const totalAmountInvoiced = sumNetInvoicedForPo(filteredInvoiceRows, row.poNumber);
        return {
          ...row,
          totalAmountInvoiced,
          poBalance: row.totalPoAmount - totalAmountInvoiced
        };
      })
      .filter((row) => {
        if (
          projectFilter !== 'all' &&
          normalizeRentalProjectLabel(row.project) !== normalizeRentalProjectLabel(projectFilter)
        ) {
          return false;
        }
        if (
          rentalCompanyFilter !== 'all' &&
          normalizeRentalCompanyLabel(row.supplier) !==
            normalizeRentalCompanyLabel(rentalCompanyFilter)
        ) {
          return false;
        }
        if (
          ((monthYearFilter !== 'all' && monthYearFilter) || statusFilter !== 'all') &&
          row.totalAmountInvoiced <= 0
        ) {
          return false;
        }
        if (!query) return true;

        return (
          row.poNumber.toLowerCase().includes(query) ||
          (row.supplier || '').toLowerCase().includes(query) ||
          (row.project || '').toLowerCase().includes(query) ||
          (row.sd || '').toLowerCase().includes(query)
        );
      });
  }, [appliedSearch, monthYearFilter, projectFilter, rentalCompanyFilter, statusFilter, poRows, filteredInvoiceRows]);

  const summary = useMemo(() => {
    const totalPoAmount = filteredPoRows.reduce((sum, row) => sum + row.totalPoAmount, 0);
    const totalPoInvoiced = filteredInvoiceRows.reduce((sum, row) => sum + rentalNet(row.amount), 0);
    const totalPoBalance = filteredPoRows.reduce((sum, row) => sum + row.poBalance, 0);
    const pendingAmountToPay = filteredInvoiceRows
      .filter((row) => !isRentalInvoicePaid(row))
      .reduce((sum, row) => sum + invoiceGrossForRow(row), 0);

    return { totalPoAmount, totalPoInvoiced, totalPoBalance, pendingAmountToPay };
  }, [filteredInvoiceRows, filteredPoRows]);

  /** Invoice table only: net (excl. VAT) totals — summed by rental company (resolved supplier). */
  const invoiceStatsByRentalCompany = useMemo(() => {
    const acc = {};
    filteredInvoiceRows.forEach((row) => {
      const key = normalizeRentalCompanyLabel(row.supplier);
      if (!key) return;
      const net = rentalNet(row.amount);
      if (!acc[key]) {
        acc[key] = { totalNet: 0, amountPaid: 0, pendingAmount: 0 };
      }
      acc[key].totalNet += net;
      if (isRentalInvoicePaid(row)) {
        acc[key].amountPaid += net;
      } else {
        acc[key].pendingAmount += net;
      }
    });
    return acc;
  }, [filteredInvoiceRows]);

  const rentalCompaniesRows = useMemo(() => {
    const fromPo = filteredPoRows.reduce((acc, row) => {
      const key = normalizeRentalCompanyLabel(row.supplier);
      if (!key) return acc;
      if (!acc[key]) {
        acc[key] = {
          rentalName: key,
          numberOfPo: 0,
          totalPoAmount: 0
        };
      }
      acc[key].numberOfPo += 1;
      acc[key].totalPoAmount += row.totalPoAmount;
      return acc;
    }, {});

    const names = new Set([
      ...Object.keys(fromPo),
      ...Object.keys(invoiceStatsByRentalCompany),
      ...rentalCompanyDocs.map((c) => normalizeRentalCompanyLabel(c.rentalName)).filter(Boolean)
    ]);

    return Array.from(names)
      .map((rentalName) => {
        const po = fromPo[rentalName];
        const inv = invoiceStatsByRentalCompany[rentalName];
        return {
          rentalName,
          numberOfPo: po?.numberOfPo ?? 0,
          totalPoAmount: po?.totalPoAmount ?? 0,
          totalAmountInvoice: inv?.totalNet ?? 0,
          amountPaid: inv?.amountPaid ?? 0,
          pendingAmount: inv?.pendingAmount ?? 0
        };
      })
      .filter(
        (row) =>
          (rentalCompanyFilter === 'all' || row.rentalName === rentalCompanyFilter) &&
          (row.numberOfPo > 0 ||
            row.totalAmountInvoice > 0 ||
            rentalCompanyDocs.some((c) => c.rentalName === row.rentalName))
      )
      .sort((a, b) => a.rentalName.localeCompare(b.rentalName));
  }, [filteredPoRows, invoiceStatsByRentalCompany, rentalCompanyDocs, rentalCompanyFilter]);

  const activeRows = useMemo(() => {
    if (activeTable === 'po') return filteredPoRows;
    if (activeTable === 'invoice') return filteredInvoiceRows;
    if (activeTable === 'companies') return rentalCompaniesRows;
    return [];
  }, [activeTable, filteredInvoiceRows, filteredPoRows, rentalCompaniesRows]);

  const totalFiltered = activeRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const indexOfLastItem = Math.min(safePage * ITEMS_PER_PAGE, totalFiltered);
  const paginatedRows = useMemo(
    () => activeRows.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE),
    [activeRows, safePage]
  );

  const paginatedPoRows = activeTable === 'po' ? paginatedRows : [];
  const paginatedInvoiceRows = activeTable === 'invoice' ? paginatedRows : [];
  const paginatedCompanyRows = activeTable === 'companies' ? paginatedRows : [];

  const formatCurrency = (value) =>
    `TZS ${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const handleExportRentalExcel = () => {
    const resolvedAll = invoiceRows.map((row) => ({
      ...row,
      supplier: poSupplierByNumber[row.poNumber] || row.supplier,
      project: row.project || poProjectByNumber[row.poNumber] || ''
    }));

    const poSheet = poRows.map((row) => {
      const totalAmountInvoiced = sumNetInvoicedForPo(resolvedAll, row.poNumber);
      return {
        'PO Number': row.poNumber,
        'Rental Company': row.supplier,
        Project: row.project,
        SD: row.sd,
        'Total PO Amount': row.totalPoAmount,
        'Total Amount Invoice (excl. VAT)': totalAmountInvoiced,
        'PO Balance': row.totalPoAmount - totalAmountInvoiced
      };
    });

    const invoiceSheet = resolvedAll.map((row) => ({
      'PR No': row.prNo || '',
      'Invoice No.': row.invoiceNumber,
      Project: row.project || '',
      'PO Number': row.poNumber,
      'Rental Company': row.supplier,
      'Invoice Date': row.invoiceDate,
      'Invoice Month': row.invoiceMonth,
      'Amount (excl. VAT)': rentalNet(row.amount),
      VAT: invoiceVatForRow(row),
      'Invoice Amount (incl. VAT)': invoiceGrossForRow(row),
      Status: row.status,
      'Date Paid': row.datePaid || '',
      Remark: row.remark || ''
    }));

    const invoiceStatsFull = {};
    resolvedAll.forEach((row) => {
      const key = row.supplier;
      if (!key) return;
      const net = rentalNet(row.amount);
      if (!invoiceStatsFull[key]) {
        invoiceStatsFull[key] = { totalNet: 0, amountPaid: 0, pendingAmount: 0 };
      }
      invoiceStatsFull[key].totalNet += net;
      if (isRentalInvoicePaid(row)) {
        invoiceStatsFull[key].amountPaid += net;
      } else {
        invoiceStatsFull[key].pendingAmount += net;
      }
    });

    const fromPoFull = poRows.reduce((acc, row) => {
      const key = row.supplier;
      if (!acc[key]) {
        acc[key] = { rentalName: key, numberOfPo: 0, totalPoAmount: 0 };
      }
      acc[key].numberOfPo += 1;
      acc[key].totalPoAmount += row.totalPoAmount;
      return acc;
    }, {});

    const companyNames = new Set([
      ...Object.keys(fromPoFull),
      ...Object.keys(invoiceStatsFull),
      ...rentalCompanyDocs.map((c) => c.rentalName)
    ]);

    const companiesSheet = Array.from(companyNames)
      .map((rentalName) => {
        const po = fromPoFull[rentalName];
        const inv = invoiceStatsFull[rentalName];
        return {
          'Rental Name': rentalName,
          'Number of PO': po?.numberOfPo ?? 0,
          'Total PO Amount': po?.totalPoAmount ?? 0,
          'Total Amount Invoice (excl. VAT)': inv?.totalNet ?? 0,
          'Amount Paid (excl. VAT)': inv?.amountPaid ?? 0,
          'Pending Amount (excl. VAT)': inv?.pendingAmount ?? 0
        };
      })
      .filter(
        (row) =>
          row['Number of PO'] > 0 ||
          row['Total Amount Invoice (excl. VAT)'] > 0 ||
          rentalCompanyDocs.some((c) => c.rentalName === row['Rental Name'])
      )
      .sort((a, b) => a['Rental Name'].localeCompare(b['Rental Name']));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(poSheet), 'Rental PO Tracking');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(invoiceSheet), 'Rental Invoice Tracking');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(companiesSheet), 'Rental Companies');

    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Rental_Payment_Trucking_${dateStr}.xlsx`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchText);
  };

  const clearFilters = () => {
    setSearchText('');
    setAppliedSearch('');
    setProjectFilter('all');
    setRentalCompanyFilter('all');
    setStatusFilter('all');
    setMonthYearFilter('all');
  };

  const cancelEditPrNo = () => {
    setEditingPrInvoiceNumber('');
    setEditPrNoValue('');
  };

  const cancelEditPoField = () => {
    setEditingPoNumber('');
    setEditingPoField('');
    setEditPoDraft('');
  };

  const startEditPoField = (row, field) => {
    if (poFieldSaving) return;
    setEditingPoNumber(row.poNumber);
    setEditingPoField(field);
    if (field === 'totalPoAmount') {
      setEditPoDraft(
        Number.isFinite(row.totalPoAmount) && row.totalPoAmount !== 0
          ? String(row.totalPoAmount)
          : ''
      );
    } else {
      setEditPoDraft(row.sd || '');
    }
  };

  const commitPoField = async (poNumber, field) => {
    const existing = poRows.find((r) => r.poNumber === poNumber);
    if (!existing || !existing.documentId) {
      cancelEditPoField();
      return;
    }

    let nextSd = existing.sd;
    let nextTotal = existing.totalPoAmount;

    if (field === 'sd') {
      nextSd = editPoDraft.trim();
      if (!nextSd) {
        window.alert('SD is required.');
        return;
      }
      if (nextSd === String(existing.sd || '').trim()) {
        cancelEditPoField();
        return;
      }
    } else if (field === 'totalPoAmount') {
      const raw = String(editPoDraft).replace(/,/g, '').trim();
      const parsed = parseFloat(raw);
      if (!Number.isFinite(parsed) || parsed < 0) {
        window.alert('Total PO amount must be a valid number (0 or greater).');
        return;
      }
      if (parsed === Number(existing.totalPoAmount)) {
        cancelEditPoField();
        return;
      }
      nextTotal = parsed;
    } else {
      cancelEditPoField();
      return;
    }

    const updated = {
      ...existing,
      sd: nextSd,
      totalPoAmount: nextTotal
    };

    try {
      setPoFieldSaving(true);
      await updateRentalPo(existing.documentId, updated);
      setPoRows((prev) => prev.map((row) => (row.poNumber === poNumber ? updated : row)));
      cancelEditPoField();
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to save PO.');
    } finally {
      setPoFieldSaving(false);
    }
  };

  const cancelEditInvoiceMonth = () => {
    setEditingMonthInvoiceNumber('');
    setEditInvoiceMonthYm('');
  };

  const startEditInvoiceMonth = (row) => {
    if (editingInvoiceNumber) return;
    cancelEditPrNo();
    setEditingMonthInvoiceNumber(row.invoiceNumber);
    setEditInvoiceMonthYm(invoiceMonthToYm(row));
  };

  const commitInvoiceMonth = async (invoiceNumber) => {
    const existing = invoiceRows.find((r) => r.invoiceNumber === invoiceNumber);
    if (!existing) {
      cancelEditInvoiceMonth();
      return;
    }
    const ym = (editInvoiceMonthYm || '').trim();
    if (!ym) {
      window.alert('Choose an invoice month.');
      return;
    }
    const invoiceMonth = formatInvoiceMonthFromYm(ym);
    if (!invoiceMonth) {
      window.alert('Invalid invoice month.');
      return;
    }
    const invoiceDate = `${ym}-01`;
    if (
      invoiceMonth === String(existing.invoiceMonth || '').trim() &&
      invoiceDate === String(existing.invoiceDate || '').trim().slice(0, 10)
    ) {
      cancelEditInvoiceMonth();
      return;
    }
    const updated = {
      ...existing,
      invoiceMonth,
      invoiceDate,
      supplier: poSupplierByNumber[existing.poNumber] || existing.supplier
    };
    try {
      if (existing.documentId) {
        await updateRentalInvoice(existing.documentId, updated);
      }
      setInvoiceRows((prev) =>
        prev.map((row) => (row.invoiceNumber === invoiceNumber ? updated : row))
      );
      cancelEditInvoiceMonth();
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to save invoice month.');
    }
  };

  const startEditPrNo = (row) => {
    if (editingInvoiceNumber) return;
    cancelEditInvoiceMonth();
    setEditingPrInvoiceNumber(row.invoiceNumber);
    setEditPrNoValue(row.prNo || '');
  };

  const commitPrNo = async (invoiceNumber) => {
    const existing = invoiceRows.find((r) => r.invoiceNumber === invoiceNumber);
    if (!existing) {
      cancelEditPrNo();
      return;
    }
    const trimmed = editPrNoValue.trim();
    if (trimmed === String(existing.prNo || '').trim()) {
      cancelEditPrNo();
      return;
    }
    const updated = {
      ...existing,
      prNo: trimmed,
      supplier: poSupplierByNumber[existing.poNumber] || existing.supplier
    };
    try {
      if (existing.documentId) {
        await updateRentalInvoice(existing.documentId, updated);
      }
      setInvoiceRows((prev) =>
        prev.map((row) => (row.invoiceNumber === invoiceNumber ? updated : row))
      );
      cancelEditPrNo();
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to save PR No.');
    }
  };

  const startEditInvoice = (row) => {
    cancelEditPrNo();
    cancelEditInvoiceMonth();
    setEditingInvoiceNumber(row.invoiceNumber);
    setEditStatus(row.status || 'Pending Signature');
    setEditDatePaid(row.datePaid || '');
  };

  const cancelEditInvoice = () => {
    cancelEditPrNo();
    cancelEditInvoiceMonth();
    setEditingInvoiceNumber('');
    setEditStatus('Pending Signature');
    setEditDatePaid('');
  };

  const saveEditInvoice = async (invoiceNumber) => {
    const normalizedDatePaid = editStatus === 'Paid' ? editDatePaid : '';
    const existing = invoiceRows.find((r) => r.invoiceNumber === invoiceNumber);
    if (!existing) return;
    const updated = {
      ...existing,
      status: editStatus,
      datePaid: normalizedDatePaid,
      supplier: poSupplierByNumber[existing.poNumber] || existing.supplier
    };
    try {
      if (existing.documentId) {
        await updateRentalInvoice(existing.documentId, updated);
      }
      setInvoiceRows((prev) =>
        prev.map((row) => (row.invoiceNumber === invoiceNumber ? updated : row))
      );
      cancelEditInvoice();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteEditInvoice = async (invoiceNumber) => {
    cancelEditPrNo();
    const existing = invoiceRows.find((r) => r.invoiceNumber === invoiceNumber);
    if (!existing) return;
    const ok = window.confirm(
      `Delete Invoice No. ${existing.invoiceNumber}? This cannot be undone.`
    );
    if (!ok) return;
    if (!existing.documentId) {
      window.alert('This invoice has no document id and cannot be deleted from the server.');
      return;
    }
    try {
      await deleteRentalInvoice(existing.documentId);
      setInvoiceRows((prev) => prev.filter((row) => row.invoiceNumber !== invoiceNumber));
      cancelEditInvoice();
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to delete invoice.');
    }
  };

  const openRecordPoModal = () => {
    setRecordPoError('');
    setNewPoForm({
      poNumber: '',
      supplier: 'Smiles',
      project: '',
      sd: '',
      totalPoAmount: ''
    });
    setShowRecordPoModal(true);
  };

  const closeRecordPoModal = () => {
    setShowRecordPoModal(false);
    setRecordPoError('');
  };

  const openRecordInvoiceModal = () => {
    setRecordInvoiceError('');
    setNewInvoiceForm({
      invoiceNumber: '',
      prNo: '',
      poNumber: '',
      invoiceDate: '',
      invoiceMonthYm: '',
      amount: '',
      vatApplies: true,
      status: 'Pending Signature',
      datePaid: ''
    });
    setShowRecordInvoiceModal(true);
  };

  const closeRecordInvoiceModal = () => {
    setShowRecordInvoiceModal(false);
    setRecordInvoiceError('');
  };

  const updateNewInvoiceForm = (field, value) => {
    setNewInvoiceForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateNewPoForm = (field, value) => {
    setNewPoForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRecordPoSubmit = async (e) => {
    e.preventDefault();
    setRecordPoError('');
    const poNumber = newPoForm.poNumber.trim();
    if (!poNumber) {
      setRecordPoError('PO number is required.');
      return;
    }
    if (poRows.some((p) => p.poNumber.toLowerCase() === poNumber.toLowerCase())) {
      setRecordPoError('This PO number already exists.');
      return;
    }
    const supplier = normalizeRentalCompanyLabel(newPoForm.supplier);
    if (!supplier) {
      setRecordPoError('Rental company is required.');
      return;
    }
    const project = normalizeRentalProjectLabel(newPoForm.project) || (newPoForm.project || '').trim();
    if (!project) {
      setRecordPoError('Project is required.');
      return;
    }
    const sd = (newPoForm.sd || '').trim();
    if (!sd) {
      setRecordPoError('SD is required.');
      return;
    }
    const rawAmount = String(newPoForm.totalPoAmount).replace(/,/g, '').trim();
    const totalPoAmount = parseFloat(rawAmount);
    if (!Number.isFinite(totalPoAmount) || totalPoAmount <= 0) {
      setRecordPoError('Total PO amount must be a number greater than zero.');
      return;
    }

    try {
      const created = await createRentalPo({
        poNumber,
        supplier,
        project,
        sd,
        totalPoAmount
      });
      setPoRows((prev) => [...prev, created]);
      closeRecordPoModal();
    } catch (err) {
      const code = err?.code;
      const msg = err?.message || String(err);
      if (code === 409 || msg.includes('409') || msg.toLowerCase().includes('unique')) {
        setRecordPoError('This PO number already exists.');
      } else {
        setRecordPoError(msg || 'Failed to save PO.');
      }
    }
  };

  const handleRecordInvoiceSubmit = async (e) => {
    e.preventDefault();
    setRecordInvoiceError('');
    const invoiceNumber = (newInvoiceForm.invoiceNumber || '').trim();
    if (!invoiceNumber) {
      setRecordInvoiceError('Invoice No. is required.');
      return;
    }
    if (invoiceRows.some((r) => r.invoiceNumber.toLowerCase() === invoiceNumber.toLowerCase())) {
      setRecordInvoiceError('This Invoice No. already exists.');
      return;
    }
    const poNumber = (newInvoiceForm.poNumber || '').trim();
    if (!poNumber) {
      setRecordInvoiceError('PO number is required.');
      return;
    }
    const po = poRows.find((p) => p.poNumber === poNumber);
    if (!po) {
      setRecordInvoiceError('Select a PO that exists in PO tracking.');
      return;
    }
    const invoiceDate = (newInvoiceForm.invoiceDate || '').trim();
    if (!invoiceDate) {
      setRecordInvoiceError('Invoice date is required.');
      return;
    }
    const invoiceMonthYm = (newInvoiceForm.invoiceMonthYm || '').trim();
    if (!invoiceMonthYm) {
      setRecordInvoiceError('Invoice month is required.');
      return;
    }
    const invoiceMonth = formatInvoiceMonthFromYm(invoiceMonthYm);
    if (!invoiceMonth) {
      setRecordInvoiceError('Invalid invoice month.');
      return;
    }
    const rawAmt = String(newInvoiceForm.amount).replace(/,/g, '').trim();
    const amount = parseFloat(rawAmt);
    if (!Number.isFinite(amount) || amount <= 0) {
      setRecordInvoiceError('Amount must be a number greater than zero.');
      return;
    }
    const status = newInvoiceForm.status || 'Pending Signature';
    const normalizedDatePaid = status === 'Paid' ? (newInvoiceForm.datePaid || '').trim() : '';
    if (status === 'Paid' && !normalizedDatePaid) {
      setRecordInvoiceError('Date paid is required when status is Paid.');
      return;
    }

    const supplier = poSupplierByNumber[poNumber] || po.supplier;
    const vatApplies = Boolean(newInvoiceForm.vatApplies);
    const payload = {
      invoiceNumber,
      prNo: (newInvoiceForm.prNo || '').trim(),
      poNumber,
      supplier,
      project: po.project,
      invoiceDate,
      invoiceMonth,
      amount,
      vatApplies,
      status,
      datePaid: normalizedDatePaid,
      remark: ''
    };
    try {
      const created = await createRentalInvoice(payload);
      setInvoiceRows((prev) => [...prev, created]);
      closeRecordInvoiceModal();
    } catch (err) {
      const code = err?.code;
      const msg = err?.message || String(err);
      if (code === 409 || msg.includes('409') || msg.toLowerCase().includes('unique')) {
        setRecordInvoiceError('This Invoice No. already exists.');
      } else {
        setRecordInvoiceError(msg || 'Failed to save invoice.');
      }
    }
  };

  const openAddRentalCompanyModal = () => {
    setAddRentalCompanyError('');
    setNewRentalCompanyName('');
    setShowAddRentalCompanyModal(true);
  };

  const closeAddRentalCompanyModal = () => {
    setShowAddRentalCompanyModal(false);
    setAddRentalCompanyError('');
  };

  const handleAddRentalCompanySubmit = async (e) => {
    e.preventDefault();
    setAddRentalCompanyError('');
    const name = newRentalCompanyName.trim();
    if (!name) {
      setAddRentalCompanyError('Rental company name is required.');
      return;
    }

    const canonical = normalizeRentalCompanyLabel(name) || name;
    const knownLower = new Set([
      ...poRows
        .map((p) => normalizeRentalCompanyLabel(p.supplier).toLowerCase())
        .filter(Boolean),
      ...invoiceRows
        .map((inv) =>
          normalizeRentalCompanyLabel(poSupplierByNumber[inv.poNumber] || inv.supplier).toLowerCase()
        )
        .filter(Boolean),
      ...rentalCompanyDocs
        .map((c) => normalizeRentalCompanyLabel(c.rentalName).toLowerCase())
        .filter(Boolean)
    ]);

    if (knownLower.has(canonical.toLowerCase())) {
      setAddRentalCompanyError('This rental company is already in the list.');
      return;
    }

    try {
      const doc = await createRentalCompany(canonical, 'manual');
      setRentalCompanyDocs((prev) => [...prev, doc]);
      closeAddRentalCompanyModal();
    } catch (err) {
      const code = err?.code;
      const msg = err?.message || String(err);
      if (code === 409 || msg.includes('409') || msg.toLowerCase().includes('unique')) {
        setAddRentalCompanyError('This rental company is already in the list.');
      } else {
        setAddRentalCompanyError(msg || 'Failed to save company.');
      }
    }
  };

  const filtersActive =
    appliedSearch.trim() !== '' ||
    projectFilter !== 'all' ||
    rentalCompanyFilter !== 'all' ||
    statusFilter !== 'all' ||
    (monthYearFilter !== 'all' && Boolean(monthYearFilter));

  const summaryCards = [
    {
      id: 'po-amount',
      label: 'Total PO amount',
      value: formatCurrency(summary.totalPoAmount),
      meta: filtersActive ? 'Filtered' : `${filteredPoRows.length} POs`,
      accent: '#3b82f6',
      icon: Wallet
    },
    {
      id: 'invoiced',
      label: 'Amount invoiced',
      value: formatCurrency(summary.totalPoInvoiced),
      meta: filtersActive ? 'Filtered · excl. VAT' : 'Excl. VAT',
      accent: '#10b981',
      icon: Receipt
    },
    {
      id: 'balance',
      label: 'PO balance',
      value: formatCurrency(summary.totalPoBalance),
      meta: filtersActive ? 'Filtered · remaining' : 'Remaining',
      accent: '#0ea5e9',
      icon: Scale
    },
    {
      id: 'pending',
      label: 'Pending to pay',
      value: formatCurrency(summary.pendingAmountToPay),
      meta: filtersActive ? 'Filtered · incl. VAT' : 'Incl. VAT',
      accent: '#f59e0b',
      icon: Clock
    }
  ];

  return (
    <div className="rp-page">
      <div className="rp-scroll">
        <div className="rp-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Fleet', to: '/sagemcom/fleet-management', hideOnMobile: true },
              { label: 'Fleet PO trucking' }
            ]}
          />

          <section className="rp-intro" aria-label="Fleet PO trucking overview">
            <div className="rp-intro-text">
              <h1 className="rp-intro-title">Fleet PO trucking</h1>
              <p className="rp-intro-subtitle">
                Track rental purchase orders, invoices, VAT and payment status, and rental company balances in one place
              </p>
            </div>
          </section>

          <div className="rp-lot-tabs" role="tablist" aria-label="Fleet PO trucking views">
            {TAB_OPTIONS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeTable === key}
                className={`rp-lot-tabs__btn${activeTable === key ? ' is-active' : ''}`}
                onClick={() => setActiveTable(key)}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {label}
              </button>
            ))}
          </div>

          {dataError ? (
            <div className="rp-error" role="alert">
              Could not load rental data: {dataError}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rp-soft-btn ml-2 h-8 gap-1.5"
                onClick={loadRentalData}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Retry
              </Button>
            </div>
          ) : null}

          {dataLoading ? (
            <div className="rp-loading" role="status">
              Loading rental data from Appwrite…
            </div>
          ) : null}

          <div className="rp-summary-cards">
            {summaryCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.id} className="rp-summary-card" style={{ '--cat-accent': card.accent }}>
                  <div className="rp-summary-card__top">
                    <span className="rp-summary-card__icon" aria-hidden>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="rp-summary-card__label">{card.label}</span>
                  </div>
                  <div className="rp-summary-card__count">{card.meta}</div>
                  <div className="rp-summary-card__amount">{card.value}</div>
                </div>
              );
            })}
          </div>

          <form className="rp-toolbar" onSubmit={handleSearch}>
            <div className="rp-search">
              <Search className="rp-search__icon h-4 w-4" aria-hidden />
              <Input
                className="rp-search__input"
                placeholder="Search by PO, Invoice No., month, or remark"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                aria-label="Search"
              />
            </div>
            <select
              className="rp-filter-select"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              aria-label="Project"
            >
              <option value="all">All projects</option>
              {projects.map((project) => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
            <select
              className="rp-filter-select"
              value={rentalCompanyFilter}
              onChange={(e) => setRentalCompanyFilter(e.target.value)}
              aria-label="Rental company"
            >
              <option value="all">All companies</option>
              {rentalCompanies.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="rp-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Status"
            >
              <option value="all">All statuses</option>
              {invoiceStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <input
              type="month"
              className="rp-filter-select"
              value={monthYearFilter === 'all' ? '' : monthYearFilter}
              onChange={(e) => setMonthYearFilter(e.target.value || 'all')}
              title="Filter invoices by calendar month of invoice date"
              aria-label="Invoice month"
            />
            <div className="rp-toolbar__spacer" aria-hidden="true" />
            <Button type="submit" size="sm" className="h-9">
              Search
            </Button>
            <Button type="button" variant="ghost" size="sm" className="rp-soft-btn h-9" onClick={clearFilters}>
              Clear
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rp-soft-btn h-9 gap-1.5"
              onClick={handleExportRentalExcel}
              disabled={dataLoading || Boolean(dataError)}
              title="Download one workbook with PO, invoice, and company sheets (all data)"
            >
              <Download className="h-3.5 w-3.5" />
              Export Excel
            </Button>
          </form>

          {activeTable === 'po' && (
            <div className="rp-table-section">
              <div className="rp-table-section__head">
                <h2>
                  Rental PO Tracking
                  <span className="rp-table-section__count">{filteredPoRows.length}</span>
                </h2>
                <Button type="button" className="rp-create-btn h-9 gap-1.5" onClick={openRecordPoModal}>
                  <Plus className="h-4 w-4" />
                  Record PO
                </Button>
              </div>
              <div className="rp-table-shell">
                <div className="rp-table-wrapper">
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th>PO Number</th>
                        <th>Rental Company</th>
                        <th>Project</th>
                        <th>SD</th>
                        <th>Total PO Amount</th>
                        <th>Total Amount Invoice (excl. VAT)</th>
                        <th>PO Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataLoading ? (
                        <tr>
                          <td colSpan={7} className="rp-empty-row">
                            Loading rental PO data…
                          </td>
                        </tr>
                      ) : paginatedPoRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="rp-empty-row">
                            {poRows.length === 0
                              ? 'No Rental PO Tracking data found. Use Record PO or add POs in Car Rental Invoicing (they sync automatically).'
                              : 'No POs match the current filters. Click Clear to show all rows.'}
                          </td>
                        </tr>
                      ) : (
                        paginatedPoRows.map((row) => {
                          const isEditingSd =
                            editingPoNumber === row.poNumber && editingPoField === 'sd';
                          const isEditingAmount =
                            editingPoNumber === row.poNumber && editingPoField === 'totalPoAmount';
                          const balanceLow = row.poBalance < PO_BALANCE_LOW_THRESHOLD;
                          return (
                          <tr key={row.poNumber}>
                            <td>{row.poNumber}</td>
                            <td>{row.supplier}</td>
                            <td>
                              <RentalProjectBadge project={row.project} />
                            </td>
                            <td className="rp-po-edit-cell">
                              {isEditingSd ? (
                                <input
                                  type="text"
                                  className="rp-invoice-edit-input"
                                  value={editPoDraft}
                                  onChange={(e) => setEditPoDraft(e.target.value)}
                                  onBlur={() => commitPoField(row.poNumber, 'sd')}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      cancelEditPoField();
                                    }
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  autoComplete="off"
                                  autoFocus
                                  disabled={poFieldSaving}
                                  aria-label={`SD for PO ${row.poNumber}`}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="rp-po-edit-hit"
                                  onClick={() => startEditPoField(row, 'sd')}
                                  disabled={poFieldSaving}
                                  title="Click to edit SD"
                                >
                                  {row.sd || '—'}
                                </button>
                              )}
                            </td>
                            <td className="rp-value rp-po-edit-cell">
                              {isEditingAmount ? (
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="rp-invoice-edit-input rp-po-amount-input"
                                  value={editPoDraft}
                                  onChange={(e) => setEditPoDraft(e.target.value)}
                                  onBlur={() => commitPoField(row.poNumber, 'totalPoAmount')}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      cancelEditPoField();
                                    }
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  autoComplete="off"
                                  autoFocus
                                  disabled={poFieldSaving}
                                  aria-label={`Total PO amount for ${row.poNumber}`}
                                />
                              ) : (
                                <button
                                  type="button"
                                  className="rp-po-edit-hit rp-po-edit-hit--value"
                                  onClick={() => startEditPoField(row, 'totalPoAmount')}
                                  disabled={poFieldSaving}
                                  title="Click to edit Total PO amount"
                                >
                                  {formatCurrency(row.totalPoAmount)}
                                </button>
                              )}
                            </td>
                            <td className="rp-value">{formatCurrency(row.totalAmountInvoiced)}</td>
                            <td
                              className={`rp-value${balanceLow ? ' rp-po-balance-low' : ''}`}
                              title={
                                balanceLow
                                  ? `PO balance below ${formatCurrency(PO_BALANCE_LOW_THRESHOLD)}`
                                  : undefined
                              }
                            >
                              {formatCurrency(row.poBalance)}
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <RpPagination
                totalFiltered={totalFiltered}
                safePage={safePage}
                totalPages={totalPages}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                onPageChange={setCurrentPage}
              />
            </div>
          )}

          {activeTable === 'invoice' && (
            <div className="rp-table-section">
              <div className="rp-table-section__head">
                <h2>
                  Rental Invoice Tracking
                  <span className="rp-table-section__count">{filteredInvoiceRows.length}</span>
                </h2>
                <Button type="button" className="rp-create-btn h-9 gap-1.5" onClick={openRecordInvoiceModal}>
                  <Plus className="h-4 w-4" />
                  Record Invoice
                </Button>
              </div>
              <div className="rp-table-shell">
                <div className="rp-table-wrapper">
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th>PR No</th>
                        <th>Invoice No.</th>
                        <th>Project</th>
                        <th>PO Number</th>
                        <th>Rental Company</th>
                        <th>Invoice Date</th>
                        <th>Invoice Month</th>
                        <th>Amount</th>
                        <th>VAT</th>
                        <th>Invoice Amount</th>
                        <th>Status</th>
                        <th>Date Paid</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedInvoiceRows.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="rp-empty-row">
                            No invoice data found.
                          </td>
                        </tr>
                      ) : (
                        paginatedInvoiceRows.map((row) => {
                          const vat = invoiceVatForRow(row);
                          const invoiceAmount = invoiceGrossForRow(row);
                          const isEditing = editingInvoiceNumber === row.invoiceNumber;
                          const isEditingPr = editingPrInvoiceNumber === row.invoiceNumber;
                          const isEditingMonth = editingMonthInvoiceNumber === row.invoiceNumber;
                          return (
                            <tr key={row.invoiceNumber}>
                              <td className="rp-invoice-pr-cell">
                                {isEditingPr ? (
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="rp-invoice-edit-input rp-invoice-pr-input"
                                    value={editPrNoValue}
                                    onChange={(e) => setEditPrNoValue(e.target.value)}
                                    onBlur={() => commitPrNo(row.invoiceNumber)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        e.preventDefault();
                                        cancelEditPrNo();
                                      }
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    autoComplete="off"
                                    autoFocus
                                    disabled={isEditing}
                                    aria-label="PR No"
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    className="rp-invoice-pr-hit"
                                    onClick={() => startEditPrNo(row)}
                                    disabled={isEditing}
                                    title={isEditing ? undefined : 'Click to edit PR No'}
                                  >
                                    {row.prNo ? row.prNo : '—'}
                                  </button>
                                )}
                              </td>
                              <td>{row.invoiceNumber}</td>
                              <td>
                                <RentalProjectBadge project={row.project} />
                              </td>
                              <td>{row.poNumber}</td>
                              <td className="rp-invoice-company-cell">
                                <span
                                  className={`rp-invoice-company-trunc${
                                    String(row.supplier || '').trim() ? '' : ' rp-invoice-company-trunc--empty'
                                  }`}
                                  title={String(row.supplier || '').trim() || undefined}
                                >
                                  {rentalCompanyCellShort(row.supplier)}
                                </span>
                              </td>
                              <td>{row.invoiceDate}</td>
                              <td className="rp-invoice-month-cell">
                                {isEditingMonth ? (
                                  <input
                                    type="month"
                                    className="rp-invoice-edit-input rp-invoice-month-input"
                                    value={editInvoiceMonthYm}
                                    onChange={(e) => setEditInvoiceMonthYm(e.target.value)}
                                    onBlur={() => commitInvoiceMonth(row.invoiceNumber)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        e.preventDefault();
                                        cancelEditInvoiceMonth();
                                      }
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    autoFocus
                                    disabled={isEditing}
                                    aria-label="Invoice month"
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    className="rp-invoice-month-hit"
                                    onClick={() => startEditInvoiceMonth(row)}
                                    disabled={isEditing}
                                    title={isEditing ? undefined : 'Click to edit invoice month'}
                                  >
                                    {row.invoiceMonth || '—'}
                                  </button>
                                )}
                              </td>
                              <td className="rp-value">{formatCurrency(row.amount)}</td>
                              <td className="rp-value">{formatCurrency(vat)}</td>
                              <td className="rp-value">{formatCurrency(invoiceAmount)}</td>
                              <td>
                                {isEditing ? (
                                  <select
                                    value={editStatus}
                                    onChange={(e) => {
                                      const nextStatus = e.target.value;
                                      setEditStatus(nextStatus);
                                      if (nextStatus !== 'Paid') setEditDatePaid('');
                                    }}
                                    className="rp-invoice-edit-input"
                                  >
                                    <option value="Paid">Paid</option>
                                    <option value="Pending Signature">Pending Signature</option>
                                    <option value="Admin review">Admin review</option>
                                    <option value="Pending Payment">Pending Payment</option>
                                  </select>
                                ) : (
                                  <span className={`rp-status-badge rp-status-${getStatusClass(row.status)}`}>
                                    {row.status}
                                  </span>
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <input
                                    type="date"
                                    value={editDatePaid}
                                    onChange={(e) => setEditDatePaid(e.target.value)}
                                    className="rp-invoice-edit-input"
                                    disabled={editStatus !== 'Paid'}
                                  />
                                ) : (
                                  row.datePaid || '-'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <div className="rp-edit-actions">
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="h-8 gap-1"
                                      onClick={() => saveEditInvoice(row.invoiceNumber)}
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="rp-soft-btn h-8 gap-1"
                                      onClick={cancelEditInvoice}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                      Cancel
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      size="sm"
                                      className="h-8 gap-1"
                                      onClick={() => deleteEditInvoice(row.invoiceNumber)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                      Delete
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="rp-soft-btn h-8 gap-1.5"
                                    onClick={() => startEditInvoice(row)}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                    Edit
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <RpPagination
                totalFiltered={totalFiltered}
                safePage={safePage}
                totalPages={totalPages}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                onPageChange={setCurrentPage}
              />
            </div>
          )}

          {activeTable === 'companies' && (
            <div className="rp-table-section">
              <div className="rp-table-section__head">
                <h2>
                  Rental Companies
                  <span className="rp-table-section__count">{rentalCompaniesRows.length}</span>
                </h2>
                <Button type="button" className="rp-create-btn h-9 gap-1.5" onClick={openAddRentalCompanyModal}>
                  <Plus className="h-4 w-4" />
                  Add rental company
                </Button>
              </div>
              <div className="rp-table-shell">
                <div className="rp-table-wrapper">
                  <table className="rp-table">
                    <thead>
                      <tr>
                        <th>Rental Name</th>
                        <th>Number of PO</th>
                        <th>Total PO Amount</th>
                        <th>Total Amount Invoice (excl. VAT)</th>
                        <th>Amount Paid (excl. VAT)</th>
                        <th>Pending Amount (excl. VAT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCompanyRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="rp-empty-row">
                            No rental company data found.
                          </td>
                        </tr>
                      ) : (
                        paginatedCompanyRows.map((row) => (
                          <tr key={row.rentalName}>
                            <td>{row.rentalName}</td>
                            <td>{row.numberOfPo}</td>
                            <td className="rp-value">{formatCurrency(row.totalPoAmount)}</td>
                            <td className="rp-value">{formatCurrency(row.totalAmountInvoice)}</td>
                            <td className="rp-value">{formatCurrency(row.amountPaid)}</td>
                            <td className="rp-value">{formatCurrency(row.pendingAmount)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <RpPagination
                totalFiltered={totalFiltered}
                safePage={safePage}
                totalPages={totalPages}
                indexOfFirstItem={indexOfFirstItem}
                indexOfLastItem={indexOfLastItem}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>

      {showRecordPoModal ? (
        <div className="rp-modal-overlay" role="presentation" onClick={closeRecordPoModal}>
          <div
            className="rp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rp-record-po-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="rp-modal__header">
              <h2 id="rp-record-po-title">Record rental PO</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rp-soft-btn h-8 w-8"
                onClick={closeRecordPoModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleRecordPoSubmit} className="rp-modal__form">
              <p className="rp-modal__hint">
                Same fields as PO tracking table. Total PO amount is the full PO value (excl. invoice lines until
                added).
              </p>
              {recordPoError && <div className="rp-modal-error">{recordPoError}</div>}
              <div className="rp-field">
                <Label htmlFor="new-po-number">PO number *</Label>
                <Input
                  id="new-po-number"
                  value={newPoForm.poNumber}
                  onChange={(e) => updateNewPoForm('poNumber', e.target.value)}
                  placeholder="e.g. PO-RT-004"
                  autoComplete="off"
                />
              </div>
              <div className="rp-field-row">
                <div className="rp-field">
                  <Label htmlFor="new-po-supplier">Rental company *</Label>
                  <select
                    id="new-po-supplier"
                    className="rp-modal-select"
                    value={newPoForm.supplier}
                    onChange={(e) => updateNewPoForm('supplier', e.target.value)}
                  >
                    {poSupplierOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rp-field">
                  <Label htmlFor="new-po-project">Project *</Label>
                  <select
                    id="new-po-project"
                    className="rp-modal-select"
                    value={newPoForm.project}
                    onChange={(e) => updateNewPoForm('project', e.target.value)}
                  >
                    <option value="">Select project</option>
                    {RENTAL_PO_PROJECT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="rp-field-row">
                <div className="rp-field">
                  <Label htmlFor="new-po-sd">SD *</Label>
                  <Input
                    id="new-po-sd"
                    value={newPoForm.sd}
                    onChange={(e) => updateNewPoForm('sd', e.target.value)}
                    placeholder="e.g. LOT 8"
                    autoComplete="off"
                  />
                </div>
                <div className="rp-field">
                  <Label htmlFor="new-po-amount">Total PO amount *</Label>
                  <Input
                    id="new-po-amount"
                    type="text"
                    inputMode="decimal"
                    value={newPoForm.totalPoAmount}
                    onChange={(e) => updateNewPoForm('totalPoAmount', e.target.value)}
                    placeholder="e.g. 5000000"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="rp-modal__actions">
                <Button type="button" variant="ghost" className="rp-soft-btn" onClick={closeRecordPoModal}>
                  Cancel
                </Button>
                <Button type="submit">Save PO</Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showRecordInvoiceModal ? (
        <div className="rp-modal-overlay" role="presentation" onClick={closeRecordInvoiceModal}>
          <div
            className="rp-modal rp-modal--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rp-record-invoice-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="rp-modal__header">
              <h2 id="rp-record-invoice-title">Record rental invoice</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rp-soft-btn h-8 w-8"
                onClick={closeRecordInvoiceModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleRecordInvoiceSubmit} className="rp-modal__form">
              <p className="rp-modal__hint">
                PO must exist in PO tracking. Amount is net (excl. VAT) when VAT applies; otherwise it is the full
                line amount with no VAT column.
              </p>
              {recordInvoiceError && <div className="rp-modal-error">{recordInvoiceError}</div>}
              <div className="rp-field-row">
                <div className="rp-field">
                  <Label htmlFor="new-inv-number">Invoice No. *</Label>
                  <Input
                    id="new-inv-number"
                    value={newInvoiceForm.invoiceNumber}
                    onChange={(e) => updateNewInvoiceForm('invoiceNumber', e.target.value)}
                    placeholder="e.g. INV-RT-001"
                    autoComplete="off"
                  />
                </div>
                <div className="rp-field">
                  <Label htmlFor="new-inv-pr">PR No</Label>
                  <Input
                    id="new-inv-pr"
                    value={newInvoiceForm.prNo}
                    onChange={(e) => updateNewInvoiceForm('prNo', e.target.value)}
                    placeholder="e.g. 4521"
                    inputMode="numeric"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="rp-field-row">
                <div className="rp-field">
                  <Label htmlFor="new-inv-po">PO number *</Label>
                  <select
                    id="new-inv-po"
                    className="rp-modal-select"
                    value={newInvoiceForm.poNumber}
                    onChange={(e) => updateNewInvoiceForm('poNumber', e.target.value)}
                  >
                    <option value="">Select PO</option>
                    {poRows.map((p) => (
                      <option key={p.poNumber} value={p.poNumber}>
                        {p.poNumber} — {p.project} ({p.supplier})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rp-field">
                  <Label htmlFor="new-inv-date">Invoice date *</Label>
                  <Input
                    id="new-inv-date"
                    type="date"
                    value={newInvoiceForm.invoiceDate}
                    onChange={(e) => updateNewInvoiceForm('invoiceDate', e.target.value)}
                  />
                </div>
              </div>
              <div className="rp-field-row">
                <div className="rp-field">
                  <Label htmlFor="new-inv-month">Invoice month *</Label>
                  <Input
                    id="new-inv-month"
                    type="month"
                    value={newInvoiceForm.invoiceMonthYm}
                    onChange={(e) => updateNewInvoiceForm('invoiceMonthYm', e.target.value)}
                  />
                </div>
                <div className="rp-field">
                  <Label htmlFor="new-inv-amount">
                    {newInvoiceForm.vatApplies ? 'Amount (excl. VAT) *' : 'Amount (no VAT) *'}
                  </Label>
                  <Input
                    id="new-inv-amount"
                    type="text"
                    inputMode="decimal"
                    value={newInvoiceForm.amount}
                    onChange={(e) => updateNewInvoiceForm('amount', e.target.value)}
                    placeholder="e.g. 1500000"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="rp-field rp-field-checkbox-row">
                <input
                  id="new-inv-vat-applies"
                  type="checkbox"
                  checked={newInvoiceForm.vatApplies}
                  onChange={(e) => updateNewInvoiceForm('vatApplies', e.target.checked)}
                />
                <Label htmlFor="new-inv-vat-applies">Apply 18% VAT (uncheck if this invoice has no VAT)</Label>
              </div>
              <div className="rp-field-row">
                <div className="rp-field">
                  <Label htmlFor="new-inv-status">Status *</Label>
                  <select
                    id="new-inv-status"
                    className="rp-modal-select"
                    value={newInvoiceForm.status}
                    onChange={(e) => {
                      const next = e.target.value;
                      setNewInvoiceForm((prev) => ({
                        ...prev,
                        status: next,
                        datePaid: next === 'Paid' ? prev.datePaid : ''
                      }));
                    }}
                  >
                    <option value="Pending Signature">Pending Signature</option>
                    <option value="Admin review">Admin review</option>
                    <option value="Pending Payment">Pending Payment</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
                {newInvoiceForm.status === 'Paid' ? (
                  <div className="rp-field">
                    <Label htmlFor="new-inv-date-paid">Date paid *</Label>
                    <Input
                      id="new-inv-date-paid"
                      type="date"
                      value={newInvoiceForm.datePaid}
                      onChange={(e) => updateNewInvoiceForm('datePaid', e.target.value)}
                    />
                  </div>
                ) : null}
              </div>
              <div className="rp-modal__actions">
                <Button type="button" variant="ghost" className="rp-soft-btn" onClick={closeRecordInvoiceModal}>
                  Cancel
                </Button>
                <Button type="submit">Save invoice</Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showAddRentalCompanyModal ? (
        <div className="rp-modal-overlay" role="presentation" onClick={closeAddRentalCompanyModal}>
          <div
            className="rp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rp-add-rental-company-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="rp-modal__header">
              <h2 id="rp-add-rental-company-title">Add rental company</h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="rp-soft-btn h-8 w-8"
                onClick={closeAddRentalCompanyModal}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleAddRentalCompanySubmit} className="rp-modal__form">
              <p className="rp-modal__hint">Enter the rental company name only. You can link POs and invoices later.</p>
              {addRentalCompanyError && <div className="rp-modal-error">{addRentalCompanyError}</div>}
              <div className="rp-field">
                <Label htmlFor="new-rental-company-name">Rental company name *</Label>
                <Input
                  id="new-rental-company-name"
                  value={newRentalCompanyName}
                  onChange={(e) => setNewRentalCompanyName(e.target.value)}
                  placeholder="e.g. ABC Car Hire"
                  autoComplete="organization"
                />
              </div>
              <div className="rp-modal__actions">
                <Button type="button" variant="ghost" className="rp-soft-btn" onClick={closeAddRentalCompanyModal}>
                  Cancel
                </Button>
                <Button type="submit">Save company</Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PaymentTrucking;
